import { AnimatePresence, motion as Motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MessageCircle, X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { http } from "../axios/axios";
import { buildProductWhatsAppMessage } from "../utils/whatsappMessage";

const phoneNumber = import.meta.env.VITE_WHATSAPP_NUMBER || "918448341881";
const labelOf = (item, fallback = "Option") =>
  item?.name ||
  item?.categoryName ||
  item?.subCategoryName ||
  item?.title ||
  item?.bannerTitle ||
  item?.label ||
  fallback;
const embedMessage = (message) => {
  const contactFooter = `\n\n🌐 Website:\nhttps://www.suraksha24x7.com/\n\n📞 Helpline:\n8448341881`;

  window.open(
    `https://wa.me/${phoneNumber}?text=${encodeURIComponent(`${message}${contactFooter}`)}`,
    "_blank",
    "noopener,noreferrer",
  );
};

const buildSecurityProductMessage = (category, subCategory) => `👋 Hi Suraksha 24X7 Team,

I am looking for information about your Security Products.

🔐 Product Section:
Security Products

📂 Category:
${labelOf(category, "")}${subCategory ? `

📌 Sub Category:
${labelOf(subCategory, "")}` : ""}

I would like assistance with:

✅ Suitable product recommendations
✅ Specifications and features
✅ Pricing
✅ Current availability
✅ Installation requirements
✅ Warranty and after-sales support

Please connect me with the appropriate security product specialist.

Thank you.
— Suraksha 24X7 Website Enquiry`;

const buildITProductMessage = (category, subCategory) => `👋 Hi Suraksha 24X7 Team,

I am interested in your IT Products range.

💻 Product Section:
IT Products

📂 Category:
${labelOf(category, "")}${subCategory ? `

📌 Sub Category:
${labelOf(subCategory, "")}` : ""}

Please help me with:

✅ Suitable IT product options
✅ Technical specifications
✅ Compatibility information
✅ Pricing and availability
✅ Installation / configuration support
✅ Warranty and technical assistance

Please connect me with an IT product expert.

Thank you.
— Suraksha 24X7 IT Product Enquiry`;

const buildSolutionMessage = (name) => `👋 Hi Suraksha 24X7 Team,

I am interested in the following security / technology solution:

🛡️ Solution:
${name}

I would like to understand:

✅ How this solution works
✅ Recommended equipment
✅ Site requirements
✅ Installation process
✅ Estimated pricing
✅ Support and maintenance options

Please arrange guidance from your solutions team.

Thank you.
— Suraksha 24X7 Solutions Enquiry`;

const buildServiceMessage = (name) => `👋 Hi Suraksha 24X7 Team,

I would like information about your service:

🛠️ Service:
${name}

Please share details regarding:

✅ Scope of service
✅ Installation / service process
✅ Pricing
✅ Availability
✅ Expected completion time
✅ After-service support

Please connect me with the appropriate service team.

Thank you.
— Suraksha 24X7 Service Enquiry`;

const buildCareerMessage = (name) => `👋 Hi Suraksha 24X7 HR Team,

I am interested in career opportunities with Suraksha 24X7.

💼 Position / Interest:
${name || "General Career Enquiry"}

I would like to know:

✅ Current openings
✅ Eligibility criteria
✅ Experience requirements
✅ Job responsibilities
✅ Application process

Please guide me regarding the next steps.

Thank you.
— Career Enquiry`;

const buildCourseMessage = (name) => `👋 Hi Suraksha 24X7 Training Team,

I am interested in the following course / training program:

🎓 Course:
${name || "Course Enquiry"}

Please share information about:

✅ Course content
✅ Duration
✅ Fees
✅ Schedule / batch timing
✅ Eligibility
✅ Certification
✅ Practical training opportunities

Please guide me with the enrollment process.

Thank you.
— Suraksha 24X7 Training Enquiry`;

const buildContactMessage = (department) => {
  const messages = {
    "Sales Enquiry": `I need assistance with:\n\n✅ Product / solution recommendations\n✅ Pricing\n✅ Availability\n✅ Installation options\n✅ Site assessment if required`,
    "Technical Support": `Please help me with:\n\n✅ Troubleshooting\n✅ Configuration assistance\n✅ Product support\n✅ Installation-related guidance`,
    "Installation Assistance": `I would like information about:\n\n✅ Site visit\n✅ Installation process\n✅ Installation charges\n✅ Required equipment\n✅ Scheduling\n✅ Post-installation support`,
    "General Enquiry": "Please guide me or connect me with the relevant department.",
  };
  const heading = department === "Sales Enquiry" ? "Sales Team" : department === "Technical Support" ? "Technical Support Team" : department === "Installation Assistance" ? "Installation Team" : "Team";
  const marker = department === "Technical Support" ? "🛠️ Support Type" : department === "Installation Assistance" ? "🔧 Requirement" : "📌 Department";
  return `👋 Hi Suraksha 24X7 ${heading},\n\nI would like to contact your team.\n\n${marker}:\n${department}\n\n${messages[department]}\n\nPlease assist me.\n\nThank you.\n— ${department}`;
};

const buildComplaintMessage = (type, details) => {
  const safeDetails = details || "I will explain the issue to your team.";
  if (type === "Feedback") return `👋 Hi Suraksha 24X7 Team,\n\nI would like to share some feedback.\n\n💬 Feedback Type:\n${type}\n\n📝 Message:\n${safeDetails}\n\nThank you for taking the time to review my feedback.\n\nRegards,\n— Suraksha 24X7 Website Visitor`;
  const support = type === "Technical Issue" ? "Technical Support Team" : type === "Service Complaint" ? "Customer Support Team" : "Support Team";
  return `⚠️ Hi Suraksha 24X7 ${support},\n\nI would like to report an issue regarding ${type.toLowerCase().replace(" complaint", "")}.\n\n${type === "Technical Issue" ? "💻 Issue Type" : type === "Installation Complaint" ? "🔧 Complaint Type" : type === "Service Complaint" ? "🛠️ Complaint Type" : "📦 Complaint Type"}:\n${type}\n\n📝 Details:\n${safeDetails}\n\nPlease assist me with:\n\n✅ Reviewing the issue\n✅ Troubleshooting or technical diagnosis\n✅ Corrective action and resolution steps\n✅ Warranty or service guidance where applicable\n\nPlease connect me with the concerned support team.\n\nThank you.\n— ${type} Request`;
};

const buildProductEnquiryMessage = (value) => {
  const isIT = value === "IT Product Enquiry";
  return `👋 Hi Suraksha 24X7 Team,\n\nI am interested in ${isIT ? "IT Products" : "Security Products"}.\n\n${isIT ? "💻" : "🔐"} Requirement:\n${value}\n\nPlease help me with:\n\n✅ Suitable ${isIT ? "IT products" : "product options"}\n✅ ${isIT ? "Specifications and compatibility" : "Pricing and specifications"}\n✅ Availability\n✅ ${isIT ? "Technical support" : "Installation support"}\n\nPlease connect me with a ${isIT ? "IT" : "security"} product specialist.\n\nThank you.\n— ${isIT ? "IT" : "Security"} Product Enquiry`;
};

const menuItems = [
  ["products", "Security Products"],
  ["it", "IT Products"],
  ["solutions", "Solutions"],
  ["services", "Services"],
  ["careers", "Careers"],
  ["courses", "Courses"],
  ["contact", "Contact"],
  ["complaint", "Complaint & Feedback"],
  ["enquiry", "Product Enquiries"],
];

const productEnquiryOptions = [
  "Product Enquiry",
  "Get Price",
  "Request Quote",
  "Availability",
  "Installation",
  "Technical Information",
  "Talk to Product Expert",
  "Product Information",
];

export default function GlobalWhatsAppChat() {
  const location = useLocation();
  const productRoute = location.pathname.match(
    /^\/(?:security-product|it-products|product)\/([^/]+)\/?$/,
  );
  const productRouteId = productRoute
    ? decodeURIComponent(productRoute[1])
    : null;
  const [isOpen, setIsOpen] = useState(false);
  const [screen, setScreen] = useState("main");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [services, setServices] = useState([]);
  const [careers, setCareers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState({
    catalog: "",
    category: null,
    subCategory: null,
    item: null,
  });
  const [complaintDetails, setComplaintDetails] = useState("");
  const [productContext, setProductContext] = useState(null);
  const currentProduct =
    productContext?.routeId === productRouteId ? productContext.product : null;

  useEffect(() => {
    if (!productRouteId) return;

    let cancelled = false;

    http
      .get(`/products/${productRouteId}`)
      .then((response) => {
        if (!cancelled) {
          setProductContext({
            routeId: productRouteId,
            product: response.data?.product || null,
          });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setProductContext({ routeId: productRouteId, product: null });
        }
        console.error("WhatsApp product context error", error);
      });

    return () => {
      cancelled = true;
    };
  }, [productRouteId]);

  const go = (next) => {
    setHistory((prev) => [...prev, screen]);
    setScreen(next);
  };
  const back = () =>
    setScreen(history.length ? history[history.length - 1] : "main") ||
    setHistory((prev) => prev.slice(0, -1));
  const loadCatalog = async (catalog) => {
    setLoading(true);
    try {
      if (catalog === "security") {
        const response = await http.get("/products/filter-options");
        setCategories(response.data?.categories || []);
        setSubCategories(response.data?.subCategories || []);
      } else {
        const [categoryResponse, subCategoryResponse] = await Promise.all([
          http.get("/ip-network/categories", { params: { limit: 1000 } }),
          http.get("/ip-network/subcategories", { params: { limit: 1000 } }),
        ]);
        setCategories(
          categoryResponse.data?.categories ||
            categoryResponse.data?.data ||
            [],
        );
        setSubCategories(
          subCategoryResponse.data?.subCategories ||
            subCategoryResponse.data?.data ||
            [],
        );
      }
      setSelected((prev) => ({
        ...prev,
        catalog,
        category: null,
        subCategory: null,
      }));
      go("categories");
    } catch (error) {
      console.error("WhatsApp catalog error", error);
    } finally {
      setLoading(false);
    }
  };

  const visibleSubCategories = useMemo(() => {
    if (!selected.category) return [];
    return subCategories.filter(
      (sub) =>
        String(sub.category?._id || sub.category || sub.categoryId) ===
        String(selected.category._id),
    );
  }, [selected.category, subCategories]);

  const chooseCategory = (category) => {
    setSelected((prev) => ({ ...prev, category, subCategory: null }));
    const ownSubs = subCategories.filter(
      (sub) =>
        String(sub.category?._id || sub.category || sub.categoryId) ===
        String(category._id),
    );
    if (ownSubs.length) go("subcategories");
    else openProductWhatsApp(category, null);
  };

  const openProductWhatsApp = (category, subCategory) => {
    embedMessage(selected.catalog === "it" ? buildITProductMessage(category, subCategory) : buildSecurityProductMessage(category, subCategory));
  };

  const loadList = async (type) => {
    setLoading(true);
    try {
      if (type === "solutions")
        setSolutions((await http.get("/solutions")).data?.data || []);
      if (type === "services")
        setServices((await http.get("/services")).data?.data || []);
      if (type === "careers")
        setCareers(
          (
            await http.get("/career/filter", {
              params: { type: "Job", limit: 50 },
            })
          ).data?.data || [],
        );
      if (type === "courses")
        setCourses(
          (
            await http.get("/career/filter", {
              params: { type: "Course", limit: 50 },
            })
          ).data?.data || [],
        );
      go(type);
    } catch (error) {
      console.error(`WhatsApp ${type} error`, error);
      go(type);
    } finally {
      setLoading(false);
    }
  };

  const openGeneral = (kind, value, details = "") => {
    if (kind === "contact") embedMessage(buildContactMessage(value));
    if (kind === "complaint") embedMessage(buildComplaintMessage(value, details));
    if (kind === "enquiry") {
      if (currentProduct) {
        embedMessage(
          buildProductWhatsAppMessage({
            product: currentProduct,
            enquiryType: value,
          }),
        );
      } else if (value === "Need Help Choosing") embedMessage(`👋 Hi Suraksha 24X7 Team,\n\nI need help choosing the right product or solution for my requirement.\n\n🔎 Requirement:\nI am not sure which product / solution would be most suitable.\n\nPlease help me with:\n\n✅ Understanding my requirement\n✅ Product / solution recommendations\n✅ Suitable options\n✅ Pricing\n✅ Installation guidance\n\nPlease connect me with a product specialist.\n\nThank you.\n— Product Consultation Request`);
      else if (value === "Bulk / Commercial Requirement") embedMessage(`👋 Hi Suraksha 24X7 Sales Team,\n\nI have a bulk / commercial requirement.\n\n🏢 Requirement Type:\nBulk / Commercial Purchase\n\nI would like assistance with:\n\n✅ Product recommendations\n✅ Bulk pricing\n✅ Project-based quotation\n✅ Availability\n✅ Installation support\n✅ Commercial warranty / AMC options\n\nPlease connect me with your commercial sales team.\n\nThank you.\n— Commercial Enquiry`);
      else embedMessage(buildProductEnquiryMessage(value));
    }
  };

  const selectMenu = (key) => {
    if (key === "products") return loadCatalog("security");
    if (key === "it") return loadCatalog("it");
    if (["solutions", "services", "careers", "courses"].includes(key))
      return loadList(key);
    go(key);
  };

  const listFor =
    screen === "solutions"
      ? solutions
      : screen === "services"
        ? services
        : screen === "careers"
          ? careers
          : courses;
  const titleFor = {
    categories:
      selected.catalog === "it"
        ? "IT Product Categories"
        : "Security Categories",
    subcategories: "Choose a Sub Category",
    solutions: "Choose a Solution",
    services: "Choose a Service",
    careers: "Current Openings",
    courses: "Choose a Course",
  };

  return (
    <div className="fixed bottom-4 right-4 z-10000 flex flex-col items-end font-sans md:bottom-6 md:right-6">
      <AnimatePresence>
        {isOpen && (
          <Motion.div
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            className="mb-3 flex h-[min(650px,calc(100vh-110px))] w-[calc(100vw-24px)] max-w-95 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          >
            <header className="shrink-0 bg-slate-950 px-5 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-300 text-slate-950">
                    <MessageCircle size={21} />
                  </span>
                  <div>
                    <p className="font-bold tracking-wide">SURAKSHA 24X7</p>
                    <p className="flex items-center gap-1 text-xs text-cyan-300">
                      <span className="h-2 w-2 rounded-full bg-emerald-400" />{" "}
                      Online
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Close chat"
                  className="rounded-full p-2 hover:bg-white/10"
                >
                  <X size={19} />
                </button>
              </div>
              <p className="mt-4 text-sm text-slate-300">
                Professional Security & IT Solutions
              </p>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4">
              <div className="mb-4 flex items-center gap-2">
                {screen !== "main" && (
                  <button
                    onClick={back}
                    className="rounded-full p-1 text-blue-700 hover:bg-blue-100"
                    aria-label="Back"
                  >
                    <ArrowLeft size={18} />
                  </button>
                )}
                <h2 className="font-semibold text-slate-900">
                  {screen === "main"
                    ? "How can we help you?"
                    : titleFor[screen] || "Choose an option"}
                </h2>
              </div>
              {loading ? (
                <div className="py-12 text-center text-sm text-slate-500">
                  Loading options...
                </div>
              ) : screen === "main" ? (
                <div className="space-y-2">
                  {menuItems.map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => selectMenu(key)}
                      className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
                    >
                      <span>{label}</span>
                      <ArrowRight size={17} className="text-blue-600" />
                    </button>
                  ))}
                </div>
              ) : screen === "categories" ? (
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category._id}
                      onClick={() => chooseCategory(category)}
                      className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 hover:border-blue-300"
                    >
                      <span>{labelOf(category)}</span>
                      <ArrowRight size={16} className="text-blue-600" />
                    </button>
                  ))}
                </div>
              ) : screen === "subcategories" ? (
                <div className="space-y-2">
                  {visibleSubCategories.map((subCategory) => (
                    <button
                      key={subCategory._id}
                      onClick={() =>
                        openProductWhatsApp(selected.category, subCategory)
                      }
                      className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 hover:border-blue-300"
                    >
                      <span>{labelOf(subCategory)}</span>
                      <ArrowRight size={16} className="text-blue-600" />
                    </button>
                  ))}
                </div>
              ) : ["solutions", "services", "careers", "courses"].includes(
                  screen,
                ) ? (
                <div className="space-y-2">
                  {listFor.length ? (
                    listFor.map((item) => (
                      <button
                        key={item._id}
                        onClick={() => {
                          setSelected((prev) => ({ ...prev, item }));
                          const itemName = labelOf(item);
                          embedMessage(screen === "solutions" ? buildSolutionMessage(itemName) : screen === "services" ? buildServiceMessage(itemName) : screen === "courses" ? buildCourseMessage(itemName) : buildCareerMessage(itemName));
                        }}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 hover:border-blue-300"
                      >
                        {labelOf(item)}
                      </button>
                    ))
                  ) : (
                    <p className="rounded-xl bg-white p-4 text-sm text-slate-500">
                      No options are currently available. Choose General Enquiry
                      below.
                    </p>
                  )}
                </div>
              ) : screen === "contact" ? (
                <div className="space-y-2">
                  {[
                    "Sales Enquiry",
                    "Technical Support",
                    "Installation Assistance",
                    "General Enquiry",
                  ].map((option) => (
                    <button
                      key={option}
                      onClick={() => openGeneral("contact", option)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 hover:border-blue-300"
                    >
                      {option}
                    </button>
                  ))}
                </div>
              ) : screen === "complaint" ? (
                <div className="space-y-3">
                  {[
                    "Product Complaint",
                    "Installation Complaint",
                    "Service Complaint",
                    "Technical Issue",
                    "Feedback",
                    "Other",
                  ].map((option) => (
                    <button
                      key={option}
                      onClick={() =>
                        setSelected((prev) => ({ ...prev, item: option }))
                      }
                      className={`w-full rounded-xl border px-4 py-3 text-left text-sm font-medium ${selected.item === option ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"}`}
                    >
                      {option}
                    </button>
                  ))}
                  <textarea
                    value={complaintDetails}
                    onChange={(event) =>
                      setComplaintDetails(event.target.value)
                    }
                    placeholder="Briefly describe your issue"
                    rows={3}
                    className="w-full rounded-xl border border-slate-200 p-3 text-sm"
                  />
                  {selected.item && (
                    <button
                      onClick={() =>
                        openGeneral(
                          "complaint",
                          selected.item,
                          complaintDetails,
                        )
                      }
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-3 font-bold text-white hover:bg-emerald-600"
                    >
                      <FaWhatsapp /> Speak to Support
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {(currentProduct
                    ? productEnquiryOptions
                    : [
                        "Security Product Enquiry",
                        "IT Product Enquiry",
                        "Need Help Choosing",
                        "Bulk / Commercial Requirement",
                      ]
                  ).map((option) => (
                    <button
                      key={option}
                      onClick={() => openGeneral("enquiry", option)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 hover:border-blue-300"
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <footer className="shrink-0 border-t border-slate-200 bg-white px-4 py-3 text-center text-xs text-slate-500">
              Choose an option and our team will assist you on WhatsApp.
            </footer>
          </Motion.div>
        )}
      </AnimatePresence>
      <Motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen((open) => !open)}
        aria-label="Open WhatsApp enquiry assistant"
        className={`flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/20 shadow-xl ${isOpen ? "bg-slate-950 text-white" : "bg-[#25D366] text-white"}`}
      >
        <FaWhatsapp size={25} />
      </Motion.button>
    </div>
  );
}
